import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';

export enum MarketplaceCommsHubTab {
    BrokerChat = 1,
    BuyerCommunications = 2,
    MeetingScheduling = 3,
    Meetings = 4,
}

export const MarketplaceCommsHubTabs = [
    {
        id: MarketplaceCommsHubTab.BrokerChat,
        content: 'Advisor Chat',
        parties: [
            { party: TimelineParty.Broker },
            { party: TimelineParty.Seller },
            { party: TimelineParty.VetValue },
            { party: TimelineParty.Observer },
        ],
        for: [
            LevelOfService.FullServiceBrokerage,
            LevelOfService.StrategicFinancialAdvisoryService,
        ],
    },
    {
        id: MarketplaceCommsHubTab.BuyerCommunications,
        content: 'Buyer Chat',
        parties: [
            { party: TimelineParty.Buyer, name: 'Seller Chat' },
            { party: TimelineParty.Seller },
            { party: TimelineParty.Broker },
            { party: TimelineParty.VetValue },
            { party: TimelineParty.Observer },
        ],
        for: [
            LevelOfService.FullServiceBrokerage,
            LevelOfService.DiscountBrokerage,
            LevelOfService.StrategicFinancialAdvisoryService,
        ],
    },
    {
        id: MarketplaceCommsHubTab.Meetings,
        content: 'Meetings',
        parties: [
            { party: TimelineParty.Buyer },
            { party: TimelineParty.Seller },
            { party: TimelineParty.Broker },
            { party: TimelineParty.VetValue },
            { party: TimelineParty.Observer },
        ],
        for: [
            LevelOfService.FullServiceBrokerage,
            LevelOfService.DiscountBrokerage,
            LevelOfService.StrategicFinancialAdvisoryService,
        ],
    },
    {
        id: MarketplaceCommsHubTab.MeetingScheduling,
        content: 'Buyer Meeting Scheduling',
        parties: [
            { party: TimelineParty.Buyer, name: 'Seller Meeting Scheduling' },
            { party: TimelineParty.Seller },
            { party: TimelineParty.Broker },
            { party: TimelineParty.VetValue },
            { party: TimelineParty.Observer },
        ],
        for: [
            LevelOfService.FullServiceBrokerage,
            LevelOfService.DiscountBrokerage,
            LevelOfService.StrategicFinancialAdvisoryService,
        ],
    },
];

export const getMarketplaceCommsHubTabs = (
    party: TimelineParty,
    levelOfService: LevelOfService,
    isAnyDataNdaSigned: boolean,
    isBrokerChatAvailable: boolean,
    events: TimelineEvent[],
    hasMeetingsOrClips: boolean,
    isAdminOrAdvisor: boolean,
    isCurrentBuyerActiveAndHasNda: boolean,
    isFF_MrktplcSellers_2_13Enabled: boolean
) => {
    const isMeetingSchedulingTabAvailable = () => {
        const provideMeetingAvailabilityEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerProvideMeetingAvailability
        );
        const agreeOnMeetingTimesEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerAgreeOnMeetingTimes
        );
        const meetingsPeriodEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetingsPeriod
        );

        const isProvideInProgress =
            provideMeetingAvailabilityEvent?.status ===
                TimelineEventStatus.InProgress ||
            provideMeetingAvailabilityEvent?.status ===
                TimelineEventStatus.Late;
        const isAgreeInProgress =
            agreeOnMeetingTimesEvent?.status ===
                TimelineEventStatus.InProgress ||
            agreeOnMeetingTimesEvent?.status === TimelineEventStatus.Late;
        const isMeetingsPeriodInProgress =
            meetingsPeriodEvent?.status ===
                TimelineEventStatus.InProgress ||
            meetingsPeriodEvent?.status === TimelineEventStatus.Late;

        if (
            party === TimelineParty.Seller ||
            party === TimelineParty.VetValue ||
            party === TimelineParty.Broker ||
            party === TimelineParty.Observer
        ) {
            return (
                (isProvideInProgress ||
                    isAgreeInProgress ||
                    isMeetingsPeriodInProgress) &&
                isFF_MrktplcSellers_2_13Enabled
            );
        }

        if (party === TimelineParty.Buyer) {
            return (
                (isAgreeInProgress || isMeetingsPeriodInProgress) &&
                isCurrentBuyerActiveAndHasNda &&
                isFF_MrktplcSellers_2_13Enabled
            );
        }

        return false;
    };

    const isMeetingsTabAvailable = () => {
        if (isAdminOrAdvisor) return true;

        if (party === TimelineParty.Seller || party === TimelineParty.Buyer) {
            return hasMeetingsOrClips;
        }

        return false;
    };

    return MarketplaceCommsHubTabs.filter(
        (tab) =>
            tab.parties.some((p) => p.party === party) &&
            tab.for.includes(levelOfService) &&
            (tab.id !== MarketplaceCommsHubTab.BuyerCommunications ||
                isAnyDataNdaSigned ||
                (party === TimelineParty.Buyer &&
                    isCurrentBuyerActiveAndHasNda)) &&
            (tab.id !== MarketplaceCommsHubTab.BrokerChat ||
                isBrokerChatAvailable) &&
            (tab.id !== MarketplaceCommsHubTab.Meetings ||
                isMeetingsTabAvailable()) &&
            (tab.id !== MarketplaceCommsHubTab.MeetingScheduling ||
                isMeetingSchedulingTabAvailable())
    ).map((t) => {
        const text =
            t.parties.find((p) => p.party === party)?.name || t.content;
        return { id: t.id, content: text };
    });
};
